if (!customElements.get('video-library')) {
  class VideoLibrary extends HTMLElement {
    connectedCallback() {
      this.blogUrl = this.dataset.blogUrl;
      this.nativeTag = this.dataset.currentTag || '';
      this.initialPath = window.location.pathname;
      this.batchSize = parseInt(this.dataset.batchSize, 10) || 24;
      this.imageRatio = parseFloat(this.dataset.imageRatio) || 1.77;
      this.showAuthor = this.dataset.showAuthor === 'true';
      this.showDate = this.dataset.showDate === 'true';
      this.countLabel = this.dataset.countLabel || 'videos';
      this.srcsetWidths = [320, 460, 600, 700, 800, 900];

      this.index = null;
      this.indexPromise = null;
      this.indexFailed = false;
      this.filtered = [];
      this.shownCount = 0;
      this.inFilterMode = false;

      this.native = this.querySelector('.js-vl-native');
      this.results = this.querySelector('.js-vl-results');
      this.grid = this.querySelector('.js-vl-grid');
      this.cardTemplate = this.querySelector('.js-vl-card-template');
      this.emptyState = this.querySelector('.js-vl-empty');
      this.moreButton = this.querySelector('.js-vl-more');
      this.statusEl = this.querySelector('.js-vl-status');
      this.searchInput = this.querySelector('.js-vl-search');
      this.fromInput = this.querySelector('.js-vl-from');
      this.toInput = this.querySelector('.js-vl-to');
      this.sortSelect = this.querySelector('.js-vl-sort');
      this.clearButtons = this.querySelectorAll('.js-vl-clear');

      this.state = {
        q: '',
        from: '',
        to: '',
        sort: 'newest',
        tag: this.nativeTag
      };

      this.tagHandleMap = {};
      this.querySelectorAll('#blog-filter-links a[data-tag]').forEach((link) => {
        if (link.dataset.tag !== '-') this.tagHandleMap[link.dataset.tag] = link.getAttribute('href');
      });

      this.bindEvents();
      this.readStateFromUrl();

      if (this.filtersActive()) {
        this.setControlValues();
        this.apply();
      } else if ('requestIdleCallback' in window) {
        window.requestIdleCallback(() => this.loadIndexForCounts(), { timeout: 4000 });
      } else {
        setTimeout(() => this.loadIndexForCounts(), 1500);
      }
    }

    bindEvents() {
      if (this.searchInput) {
        let debounceTimer = null;
        this.searchInput.addEventListener('input', () => {
          clearTimeout(debounceTimer);
          debounceTimer = setTimeout(() => {
            this.state.q = this.searchInput.value.trim();
            this.apply();
          }, 250);
        });
      }

      if (this.fromInput) {
        this.fromInput.addEventListener('change', () => {
          this.state.from = this.fromInput.value;
          this.apply();
        });
      }

      if (this.toInput) {
        this.toInput.addEventListener('change', () => {
          this.state.to = this.toInput.value;
          this.apply();
        });
      }

      if (this.sortSelect) {
        this.sortSelect.addEventListener('change', () => {
          this.state.sort = this.sortSelect.value;
          this.apply();
        });
      }

      this.clearButtons.forEach((btn) => {
        btn.addEventListener('click', this.clearFilters.bind(this));
      });

      if (this.moreButton) {
        this.moreButton.addEventListener('click', () => this.renderBatch());

        this.moreObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && !this.moreButton.hidden && this.inFilterMode) {
                this.renderBatch();
              }
            });
          },
          { rootMargin: `${window.innerHeight}px 0px` }
        );
        this.moreObserver.observe(this.moreButton);
      }

      this.addEventListener('click', (evt) => {
        if (evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey || evt.button !== 0) return;

        const filterLink = evt.target.closest('#blog-filter-links a[data-tag]');
        if (filterLink) {
          evt.preventDefault();
          this.state.tag = filterLink.dataset.tag === '-' ? '' : filterLink.dataset.tag;
          this.apply();
          return;
        }

        const cardTagLink = evt.target.closest('.card__tags a');
        if (cardTagLink) {
          const tag = this.tagFromHref(cardTagLink.getAttribute('href'));
          if (tag) {
            evt.preventDefault();
            this.state.tag = tag;
            this.apply();
            this.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      });

      this.addEventListener(
        'change',
        (evt) => {
          if (!evt.target.closest('#blog-filter-dropdown')) return;
          const selected = evt.detail && evt.detail.selectedValue;
          if (selected === undefined) return;

          evt.stopPropagation();
          this.state.tag = selected === '-' ? '' : selected;
          this.apply();
        },
        true
      );
    }

    readStateFromUrl() {
      const params = new URLSearchParams(window.location.search);
      if (params.get('q')) this.state.q = params.get('q');
      if (params.get('from')) this.state.from = params.get('from');
      if (params.get('to')) this.state.to = params.get('to');
      if (params.get('sort')) this.state.sort = params.get('sort');
      if (params.get('tag')) {
        this.state.tag = params.get('tag') === '-' ? '' : params.get('tag');
      }
    }

    setControlValues() {
      if (this.searchInput) this.searchInput.value = this.state.q;
      if (this.fromInput) this.fromInput.value = this.state.from;
      if (this.toInput) this.toInput.value = this.state.to;
      if (this.sortSelect) this.sortSelect.value = this.state.sort;
    }

    filtersActive() {
      return this.nonTagFiltersActive() || this.state.tag !== this.nativeTag;
    }

    tagFromHref(href) {
      if (!href) return null;
      const path = href.split('?')[0];
      const match = Object.keys(this.tagHandleMap)
        .find((tag) => this.tagHandleMap[tag].split('?')[0] === path);
      return match || null;
    }

    nonTagFiltersActive() {
      return Boolean(this.state.q || this.state.from || this.state.to || this.state.sort !== 'newest');
    }

    loadIndex() {
      if (this.indexPromise) return this.indexPromise;

      this.indexPromise = (async () => {
        const articles = [];
        let page = 1;
        let pages = 1;

        do {
          const response = await fetch(`${this.blogUrl}?section_id=video-library-data&page=${page}`);
          if (!response.ok) throw new Error(`Video library data request failed (${response.status})`);

          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();
          const dataEl = tmpl.content.querySelector('[data-video-library-data]');
          if (!dataEl) throw new Error('Video library data section not found in response');

          pages = parseInt(dataEl.dataset.pages, 10) || 1;
          articles.push(...JSON.parse(dataEl.textContent));
          page += 1;
        } while (page <= pages);

        this.index = articles;
        this.updateTagCounts();
        return articles;
      })();

      this.indexPromise.catch((error) => {
        this.indexFailed = true;
        console.warn('Video library index unavailable', error); // eslint-disable-line
      });

      return this.indexPromise;
    }

    async loadIndexForCounts() {
      try {
        await this.loadIndex();
      } catch (error) {
        // no-op
      }
    }

    updateTagCounts() {
      if (!this.index) return;

      const tally = {};
      this.index.forEach((article) => {
        article.tags.forEach((tag) => {
          tally[tag] = (tally[tag] || 0) + 1;
        });
      });

      this.querySelectorAll('.js-vl-tag-count').forEach((el) => {
        const tag = el.dataset.tagCount;
        const count = tag === '-' ? this.index.length : tally[tag] || 0;
        el.textContent = ` (${count})`;
        el.classList.add('is-loaded');
      });
    }

    async apply() {
      this.updateUrl();
      this.updateTagLinkStyles();
      this.toggleClearButtons();

      if (!this.filtersActive()) {
        this.exitFilterMode();
        return;
      }

      try {
        await this.loadIndex();
      } catch (error) {
        return;
      }

      this.enterFilterMode();
      this.filtered = this.filterArticles();
      this.sortArticles(this.filtered);
      this.renderResults();
    }

    filterArticles() {
      const terms = this.state.q.toLowerCase().split(/\s+/).filter(Boolean);
      const fromTs = this.state.from ? Date.parse(`${this.state.from}T00:00:00`) / 1000 : null;
      const toTs = this.state.to ? Date.parse(`${this.state.to}T23:59:59`) / 1000 : null;

      return this.index.filter((article) => {
        if (this.state.tag && !article.tags.includes(this.state.tag)) return false;
        if (fromTs && article.published < fromTs) return false;
        if (toTs && article.published > toTs) return false;

        if (terms.length) {
          const haystack = `${article.title} ${article.tags.join(' ')} ${article.excerpt} ${article.author}`.toLowerCase();
          if (!terms.every((term) => haystack.includes(term))) return false;
        }

        return true;
      });
    }

    sortArticles(articles) {
      const collator = new Intl.Collator(undefined, { sensitivity: 'base' });
      switch (this.state.sort) {
        case 'oldest':
          articles.sort((a, b) => a.published - b.published);
          break;
        case 'az':
          articles.sort((a, b) => collator.compare(a.title, b.title));
          break;
        case 'za':
          articles.sort((a, b) => collator.compare(b.title, a.title));
          break;
        default:
          articles.sort((a, b) => b.published - a.published);
      }
    }

    enterFilterMode() {
      if (this.inFilterMode) return;
      this.inFilterMode = true;
      this.native.hidden = true;
      this.results.hidden = false;

      const pagination = this.querySelector('custom-pagination');
      if (pagination) pagination.setAttribute('data-pause-infinite-scroll', 'true');
    }

    exitFilterMode() {
      if (!this.inFilterMode) return;
      this.inFilterMode = false;
      this.native.hidden = false;
      this.results.hidden = true;
      if (this.statusEl) this.statusEl.textContent = '';

      const pagination = this.querySelector('custom-pagination');
      if (pagination) pagination.setAttribute('data-pause-infinite-scroll', 'false');
    }

    renderResults() {
      this.grid.innerHTML = '';
      this.shownCount = 0;
      this.emptyState.hidden = this.filtered.length > 0;
      this.renderBatch();

      if (this.statusEl) {
        this.statusEl.textContent = `${this.filtered.length} ${this.countLabel}`;
      }
    }

    renderBatch() {
      const batch = this.filtered.slice(this.shownCount, this.shownCount + this.batchSize);
      const fragment = document.createDocumentFragment();
      batch.forEach((article) => fragment.appendChild(this.buildCard(article)));
      this.grid.appendChild(fragment);
      this.shownCount += batch.length;
      this.moreButton.hidden = this.shownCount >= this.filtered.length;
    }

    buildCard(article) {
      const card = this.cardTemplate.content.cloneNode(true);

      card.querySelectorAll('.js-vl-link').forEach((link) => {
        link.href = article.url;
      });

      const media = card.querySelector('.js-vl-media');
      if (media) {
        if (article.image) {
          const mediaFrame = media.querySelector('.media');
          mediaFrame.style.paddingTop = `${(1 / this.imageRatio) * 100}%`;

          const img = media.querySelector('.js-vl-image');
          img.src = article.image;
          img.srcset = this.srcsetWidths
            .map((width) => `${this.imageUrl(article.image, width)} ${width}w`)
            .join(', ');
          img.alt = article.imageAlt || article.title;
        } else {
          media.remove();
        }
      }

      const title = card.querySelector('.js-vl-title');
      title.textContent = article.title;

      const tagsEl = card.querySelector('.js-vl-card-tags');
      if (tagsEl) {
        article.tags.forEach((tag, i) => {
          if (i > 0) tagsEl.appendChild(document.createTextNode(', '));
          if (this.tagHandleMap[tag]) {
            const link = document.createElement('a');
            link.href = this.tagHandleMap[tag];
            link.className = 'text-current text-theme-light';
            link.textContent = tag;
            tagsEl.appendChild(link);
          } else {
            tagsEl.appendChild(document.createTextNode(tag));
          }
        });
      }

      const excerptEl = card.querySelector('.js-vl-excerpt');
      if (excerptEl) excerptEl.textContent = article.excerpt;

      const metaEl = card.querySelector('.js-vl-meta');
      if (metaEl) {
        let metaHtml = '';
        if (this.showAuthor) {
          const author = document.createElement('span');
          author.textContent = article.author;
          metaHtml += author.innerHTML + (this.showDate ? ' | ' : '');
        }
        if (this.showDate) metaHtml += article.dateHtml;
        metaEl.innerHTML = metaHtml;
      }

      return card;
    }

    imageUrl(url, width) {
      return url.replace(/([?&])width=\d+/, `$1width=${width}`);
    }

    updateTagLinkStyles() {
      this.querySelectorAll('#blog-filter-links a[data-tag]').forEach((link) => {
        const isActive = link.dataset.tag === (this.state.tag || '-');
        link.classList.toggle('font-bold', isActive);
        link.classList.toggle('underline', isActive);
      });
    }

    toggleClearButtons() {
      this.clearButtons.forEach((btn) => {
        btn.hidden = !this.filtersActive();
        btn.classList.toggle('hidden', !this.filtersActive());
      });
    }

    clearFilters() {
      this.state = {
        q: '',
        from: '',
        to: '',
        sort: 'newest',
        tag: this.nativeTag
      };
      this.setControlValues();
      this.apply();
    }

    updateUrl() {
      const params = new URLSearchParams(window.location.search);
      ['q', 'from', 'to', 'sort', 'tag'].forEach((key) => params.delete(key));

      if (this.state.q) params.set('q', this.state.q);
      if (this.state.from) params.set('from', this.state.from);
      if (this.state.to) params.set('to', this.state.to);
      if (this.state.sort !== 'newest') params.set('sort', this.state.sort);
      if (this.state.tag !== this.nativeTag) params.set('tag', this.state.tag || '-');

      const query = params.toString();
      const basePath = this.state.tag === this.nativeTag ? window.location.pathname : this.blogUrl;
      const url = `${basePath}${query ? `?${query}` : ''}`;
      window.history.replaceState(null, '', url);
    }
  }

  customElements.define('video-library', VideoLibrary);
}
